/** biome-ignore-all lint/performance/noNamespaceImport: ok */
"use client";
import { Fieldset, Form } from "@base-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import image from "#/assets/images/calc-vaccination.png";
import PageHeader from "@/components/layout/page-header";
import { Button } from "@/components/ui/button";
import ExtendedImage from "@/components/ui/extended-image";
import * as Field from "@/components/ui/form/field";
import * as Radio from "@/components/ui/form/radio";
import { RadioGroup } from "@/components/ui/form/radio-group";
import { VaccineAction } from "../_components/action";
import {
  type VaccineModel,
  type VaccineResponse,
  VaccineSchema,
} from "../_components/types";
import { ResultDialog } from "./_components/result-dialog";

const VaccineCalculation = () => {
  const { control, handleSubmit } = useForm<VaccineModel>({
    defaultValues: { gender: 0 },
    resolver: zodResolver(VaccineSchema),
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [result, setResult] = useState<VaccineResponse | null>(null);

  const mutation = useMutation({
    mutationFn: (data: VaccineModel) => VaccineAction(data),
    onSuccess: (resp) => {
      if (resp.isSuccess) {
        setResult(resp.result);
        setDialogOpen(true);
      }
    },
  });

  const onSubmit = (data: VaccineModel) => {
    mutation.mutate(data);
  };

  return (
    <div className="flex flex-col gap-4 justify-center mb-20">
      <PageHeader title="Aşı Takvimi" />
      <div className="rounded-full border border-gray-300 size-56 mx-auto mb-10">
        <ExtendedImage
          alt="ovum"
          className="rounded-full size-56"
          height={224}
          src={image}
          width={224}
        />
      </div>
      <Form
        className="w-96 mx-auto flex flex-col gap-4"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Controller
          control={control}
          name="gender"
          render={({ field, fieldState }) => {
            return (
              <Field.Root
                dirty={fieldState.isDirty}
                invalid={fieldState.invalid}
                name={field.name}
                touched={fieldState.isTouched}
              >
                <Field.Label>Cinsiyet</Field.Label>
                <Fieldset.Root
                  render={
                    <RadioGroup
                      className="gap-4"
                      inputRef={field.ref}
                      onValueChange={field.onChange}
                      value={field.value}
                    />
                  }
                >
                  <Field.Item>
                    <Field.Label className="flex flex-row gap-2 font-semibold">
                      <Radio.Root onBlur={field.onBlur} value={1}>
                        <Radio.Indicator />
                      </Radio.Root>
                      Kız
                    </Field.Label>
                  </Field.Item>
                  <Field.Item>
                    <Field.Label className="flex flex-row gap-2 font-semibold">
                      <Radio.Root onBlur={field.onBlur} value={2}>
                        <Radio.Indicator />
                      </Radio.Root>
                      Erkek
                    </Field.Label>
                  </Field.Item>
                </Fieldset.Root>
                <Field.Error match={!!fieldState.error}>
                  {fieldState.error?.message}
                </Field.Error>
              </Field.Root>
            );
          }}
        />
        <Controller
          control={control}
          name="birthDate"
          render={({ field, fieldState }) => {
            return (
              <Field.Root
                dirty={fieldState.isDirty}
                invalid={fieldState.invalid}
                name={field.name}
                touched={fieldState.isTouched}
              >
                <Field.Label>Bebeğinizin Doğum Tarihi</Field.Label>
                <Field.Control
                  onBlur={field.onBlur}
                  onValueChange={field.onChange}
                  ref={field.ref}
                  type="date"
                  value={field.value}
                />
                <Field.Error match={!!fieldState.error}>
                  {fieldState.error?.message}
                </Field.Error>
              </Field.Root>
            );
          }}
        />
        <Controller
          control={control}
          name="babyName"
          render={({ field, fieldState }) => {
            return (
              <Field.Root
                dirty={fieldState.isDirty}
                invalid={fieldState.invalid}
                name={field.name}
                touched={fieldState.isTouched}
              >
                <Field.Label>Bebeğinizin Adı</Field.Label>
                <Field.Control
                  onBlur={field.onBlur}
                  onValueChange={field.onChange}
                  placeholder="Bebek adı"
                  ref={field.ref}
                  type="text"
                  value={field.value}
                />
                <Field.Error match={!!fieldState.error}>
                  {fieldState.error?.message}
                </Field.Error>
              </Field.Root>
            );
          }}
        />

        <Button disabled={mutation.isPending} type="submit">
          {mutation.isPending ? "Hesaplanıyor..." : "Hesapla"}
        </Button>
      </Form>

      <ResultDialog
        onOpenChange={setDialogOpen}
        open={dialogOpen}
        result={result}
      />
    </div>
  );
};

export default VaccineCalculation;
